import React, {useRef} from 'react';
import {
    Box, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {parseAmountDisplay} from '@/utils/dataUtils';
import {FS_CODE, isCode} from '@/utils/fsCodes';
import {PlFsTableRow, type PlFsFlatRow} from '@/pages/components/fs-report/PlFsTableRow';
import {
    fsCompactCellPadSx,
    lastYearColumnSeparatorOnTableSx,
} from '@/pages/components/fs-report/fsColumnSx';
import {
    FsStatementTableSkeletonRows,
    buildFsPlSkeletonColumns,
} from '@/pages/components/fs-report/FsStatementTableSkeleton';
import type {PlCotInputMode} from '@/pages/components/fs-report/plFsMultiCompute';
import {useFillRemainingViewport} from '@/hooks/useFillRemainingViewport';
import {useSplitTabsTableContainerRef} from '@/components/common';

export type PlFsMultiVirtualTableProps = {
    reportHeader: React.ReactNode;
    loading: boolean;
    rows: PlFsFlatRow[];
    expanded: Record<number, boolean>;
    /** MULTI YOA headers; ignored when isFsMulti is false. */
    fsYoaList: string[];
    /**
     * true = YOA×N amount columns (MULTI);
     * false = single Current column (SINGLE) — same row chrome / MoneyField layout.
     */
    isFsMulti?: boolean;
    hideLastYearColumns: boolean;
    /** 只显示 Last Year，隐藏 Current / YOA 与 Change */
    lastYearOnly?: boolean;
    /** AI 合并 remark：fsCode → 文案 */
    lastYearRemarkByFsCode?: Record<string, string> | null;
    readOnly: boolean;
    cotInputModeCurrent: PlCotInputMode;
    cotInputModeLastYear: PlCotInputMode;
    amountDisplays: Record<number, string>;
    lastYearAmounts: Record<number, string>;
    amountDisplaysByYoa: Record<string, Record<number, string>>;
    computedAmounts: Record<number, number>;
    computedByYoa: Record<string, Record<number, number>>;
    lastYearComputed: Record<number, number>;
    isProtectedParentHeader: (item: any) => boolean;
    isEditableLeaf: (item: any, inputMode?: 'COS' | 'PUR') => boolean;
    getDisplayName: (item: any) => string;
    onToggleNode: (fsId: number) => void;
    onToggleCotCurrent: (next: PlCotInputMode) => void;
    onToggleCotLastYear: (next: PlCotInputMode) => void;
    onChangeCurrentAmount: (fsId: number, num: number) => void;
    onChangeLastYearAmount: (fsId: number, num: number) => void;
    onChangeYoaAmount: (yoa: string, fsId: number, num: number) => void;
    onFsLineClick?: (fsCode: string) => void;
    navigableFsCodes?: Set<string>;
    onAddCustomNode?: (parentItem: any) => void;
    onEditCustomNode?: (item: any) => void;
    onDeleteCustomNode?: (item: any, anchorEl: HTMLElement) => void;
    customDraftNameEn?: string;
    customDraftCurrentAmount?: number;
    customDraftLastYearAmount?: number;
    customDraftAmountsByYoa?: Record<string, number>;
    customDraftSaving?: boolean;
    onCustomDraftNameChange?: (nameEn: string) => void;
    onCustomDraftCurrentChange?: (num: number) => void;
    onCustomDraftLastYearChange?: (num: number) => void;
    onCustomDraftYoaChange?: (yoa: string, num: number) => void;
    onCustomDraftCommit?: () => void;
    onCustomDraftCancel?: () => void;
    customEditFsId?: number | null;
    customEditNameEn?: string;
    customEditSaving?: boolean;
    onCustomEditNameChange?: (nameEn: string) => void;
    onCustomEditCommit?: () => void | Promise<void>;
    onCustomEditCancel?: () => void;
};

function resolveRowAmounts(args: {
    row: PlFsFlatRow;
    computedAmounts: Record<number, number>;
    lastYearComputed: Record<number, number>;
    amountDisplays: Record<number, string>;
    lastYearAmounts: Record<number, string>;
    isProtectedParentHeader: (item: any) => boolean;
}): {computedAmount: number; lastYearComputedAmount: number} {
    const {row, computedAmounts, lastYearComputed, amountDisplays, lastYearAmounts, isProtectedParentHeader} = args;
    const {item, isParent, isSubtotal, parentItem} = row;

    let computedAmount = computedAmounts[item.fsId] ?? 0;
    if (!isParent && !isProtectedParentHeader(item) && !(item.fsId in computedAmounts)) {
        computedAmount = parseAmountDisplay(amountDisplays[item.fsId] ?? '0');
    }
    if (isSubtotal && parentItem) {
        computedAmount = computedAmounts[parentItem.fsId] ?? 0;
    }

    let lastYearComputedAmount = lastYearComputed[item.fsId] ?? 0;
    if (!isParent && !isProtectedParentHeader(item) && !(item.fsId in lastYearComputed)) {
        lastYearComputedAmount = parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0');
    }
    if (isSubtotal && parentItem) {
        lastYearComputedAmount = lastYearComputed[parentItem.fsId] ?? 0;
    }

    return {computedAmount, lastYearComputedAmount};
}

function rowKey(row: PlFsFlatRow): string {
    if (row.isCustomDraft) {
        return `custom-draft-${row.parentItem?.fsId ?? row.item?.fsId}`;
    }
    return `${row.item.fsId}-${row.isSubtotal ? 'sub' : 'row'}`;
}

/**
 * PL FS table — same scroll model as BalanceSheet FS:
 * full TableBody (no windowing). Virtual spacer rows + remount-on-scroll caused jank.
 */
export function PlFsMultiVirtualTable({
    reportHeader,
    loading,
    rows,
    expanded,
    fsYoaList,
    isFsMulti = false,
    hideLastYearColumns,
    lastYearOnly = false,
    lastYearRemarkByFsCode,
    readOnly,
    cotInputModeCurrent,
    cotInputModeLastYear,
    amountDisplays,
    lastYearAmounts,
    amountDisplaysByYoa,
    computedAmounts,
    computedByYoa,
    lastYearComputed,
    isProtectedParentHeader,
    isEditableLeaf,
    getDisplayName,
    onToggleNode,
    onToggleCotCurrent,
    onToggleCotLastYear,
    onChangeCurrentAmount,
    onChangeLastYearAmount,
    onChangeYoaAmount,
    onFsLineClick,
    navigableFsCodes,
    onAddCustomNode,
    onEditCustomNode,
    onDeleteCustomNode,
    customDraftNameEn,
    customDraftCurrentAmount,
    customDraftLastYearAmount,
    customDraftAmountsByYoa,
    customDraftSaving,
    onCustomDraftNameChange,
    onCustomDraftCurrentChange,
    onCustomDraftLastYearChange,
    onCustomDraftYoaChange,
    onCustomDraftCommit,
    onCustomDraftCancel,
    customEditFsId,
    customEditNameEn,
    customEditSaving,
    onCustomEditNameChange,
    onCustomEditCommit,
    onCustomEditCancel,
}: PlFsMultiVirtualTableProps) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const tableContainerRef = useSplitTabsTableContainerRef<HTMLDivElement>(scrollRef);
    const fillRootRef = useRef<HTMLDivElement>(null);
    const fillHeightPx = useFillRemainingViewport(fillRootRef);
    const showRemarkCol = !!lastYearOnly
        && !!lastYearRemarkByFsCode
        && Object.keys(lastYearRemarkByFsCode).length > 0;
    const amountColCount = lastYearOnly ? 0 : (isFsMulti ? fsYoaList.length : 1);
    const colSpan = 1 + (showRemarkCol ? 1 : 0) + amountColCount
        + (hideLastYearColumns ? 0 : (lastYearOnly ? 1 : 2));

    return (
        <Box
            ref={fillRootRef}
            sx={{
                width: '100%',
                height: '100%',
                maxHeight: fillHeightPx != null ? fillHeightPx : undefined,
                minHeight: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {reportHeader}
            <Divider sx={{mb: 1}}/>
            <TableContainer
                ref={tableContainerRef}
                component={Paper}
                sx={{flex: 1, minHeight: 0, overflow: 'auto'}}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        tableLayout: 'fixed',
                        width: '100%',
                        ...(!hideLastYearColumns ? lastYearColumnSeparatorOnTableSx : {}),
                    }}
                >
                    <TableHead>
                        <TableRow sx={{
                            '& .MuiTableCell-root': {
                                ...fsCompactCellPadSx,
                            },
                        }}>
                            <TableCell sx={{width: showRemarkCol ? '28%' : '36.5%'}}/>
                            {showRemarkCol && (
                                <TableCell sx={{width: '32%', fontWeight: 700}}>Remark</TableCell>
                            )}
                            {!lastYearOnly && (isFsMulti ? (
                                fsYoaList.map((yoa) => (
                                    <TableCell key={yoa} align="center" sx={{width: 140, fontWeight: 700}}>
                                        {yoa}
                                    </TableCell>
                                ))
                            ) : (
                                <TableCell align="center" sx={{width: 188, fontWeight: 700}}>Current</TableCell>
                            ))}
                            {!hideLastYearColumns && (
                                <TableCell
                                    align="center"
                                    sx={{width: isFsMulti ? 160 : 188, fontWeight: 700}}
                                >
                                    Last Year
                                </TableCell>
                            )}
                            {!hideLastYearColumns && !lastYearOnly && (
                                <TableCell align="center" sx={{width: 120, fontWeight: 700}}>Change</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <FsStatementTableSkeletonRows
                                columns={buildFsPlSkeletonColumns({
                                    hideLastYearColumns,
                                    lastYearOnly,
                                    showRemark: showRemarkCol,
                                    yoaCount: isFsMulti ? fsYoaList.length : 0,
                                })}
                            />
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={colSpan} align="center">No data</TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.map((row) => {
                            const {item, isParent, isSubtotal, isCustomDraft} = row;
                            const isExpanded = !!expanded[Number(item.fsId)];
                            const editableLeaf = !isCustomDraft && isEditableLeaf(item) && !isSubtotal;
                            const {computedAmount, lastYearComputedAmount} = resolveRowAmounts({
                                row,
                                computedAmounts,
                                lastYearComputed,
                                amountDisplays,
                                lastYearAmounts,
                                isProtectedParentHeader,
                            });
                            const displayName = isSubtotal ? '' : getDisplayName(item);
                            const shouldShowValue = !isParent || !isExpanded;
                            const editableLastYearLeaf = !readOnly && (
                                isEditableLeaf(item, cotInputModeLastYear)
                                || isCode(item, ...FS_CODE.PL.OPENING_INVENTORIES)
                            );
                            const lineFsCode = String(item?.code || '').trim();
                            const nameClickable = !!readOnly
                                && !!onFsLineClick
                                && !isParent
                                && !isSubtotal
                                && !!lineFsCode
                                && (!navigableFsCodes || navigableFsCodes.has(lineFsCode));
                            const lastYearRemark = (!isSubtotal && lineFsCode && lastYearRemarkByFsCode)
                                ? (lastYearRemarkByFsCode[lineFsCode] || '')
                                : '';

                            return (
                                <PlFsTableRow
                                    key={rowKey(row)}
                                    row={row}
                                    isExpanded={isExpanded}
                                    isFsMulti={isFsMulti}
                                    fsYoaList={fsYoaList}
                                    hideLastYearColumns={hideLastYearColumns}
                                    lastYearOnly={lastYearOnly}
                                    showRemarkCol={showRemarkCol}
                                    lastYearRemark={lastYearRemark || undefined}
                                    readOnly={readOnly}
                                    cotInputModeCurrent={cotInputModeCurrent}
                                    cotInputModeLastYear={cotInputModeLastYear}
                                    editableLeaf={editableLeaf}
                                    editableLastYearLeaf={editableLastYearLeaf}
                                    displayName={displayName}
                                    shouldShowValue={shouldShowValue}
                                    computedAmount={computedAmount}
                                    lastYearComputedAmount={lastYearComputedAmount}
                                    amountDisplays={amountDisplays}
                                    lastYearAmounts={lastYearAmounts}
                                    amountDisplaysByYoa={amountDisplaysByYoa}
                                    computedByYoa={computedByYoa}
                                    isProtectedParentHeader={isProtectedParentHeader}
                                    onToggleNode={onToggleNode}
                                    onToggleCotCurrent={onToggleCotCurrent}
                                    onToggleCotLastYear={onToggleCotLastYear}
                                    onChangeCurrentAmount={onChangeCurrentAmount}
                                    onChangeLastYearAmount={onChangeLastYearAmount}
                                    onChangeYoaAmount={onChangeYoaAmount}
                                    nameClickable={nameClickable}
                                    onNameClick={nameClickable
                                        ? () => onFsLineClick!(lineFsCode)
                                        : undefined}
                                    onAddCustomNode={onAddCustomNode}
                                    onEditCustomNode={onEditCustomNode}
                                    onDeleteCustomNode={onDeleteCustomNode}
                                    customDraftNameEn={customDraftNameEn}
                                    customDraftCurrentAmount={customDraftCurrentAmount}
                                    customDraftLastYearAmount={customDraftLastYearAmount}
                                    customDraftAmountsByYoa={customDraftAmountsByYoa}
                                    customDraftSaving={customDraftSaving}
                                    onCustomDraftNameChange={onCustomDraftNameChange}
                                    onCustomDraftCurrentChange={onCustomDraftCurrentChange}
                                    onCustomDraftLastYearChange={onCustomDraftLastYearChange}
                                    onCustomDraftYoaChange={onCustomDraftYoaChange}
                                    onCustomDraftCommit={onCustomDraftCommit}
                                    onCustomDraftCancel={onCustomDraftCancel}
                                    customEditFsId={customEditFsId}
                                    customEditNameEn={customEditNameEn}
                                    customEditSaving={customEditSaving}
                                    onCustomEditNameChange={onCustomEditNameChange}
                                    onCustomEditCommit={onCustomEditCommit}
                                    onCustomEditCancel={onCustomEditCancel}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
